import {
  Button,
  IconValidationValid,
  Stack,
  Text,
} from '@adc/parallax-component-library';
import { IconPrint } from '@adc/parallax-icons';
import { Dialog } from '@tamagui/dialog';
import { Image } from '@tamagui/image';

import QrWikipedia from '~/assets/qr-wikipedia.png';
import { ModalContent } from '~/components/ModalContent';
import { ModalOverlay } from '~/components/ModalOverlay';
import { useModal } from '~/features/modals/useModal';
import { buildTestId } from '~/utils/buildTestId';

type PatientAddedModalProps = {
  testID: string;
  patientId: string;
  patientName: string;
  inviteBy: 'none' | 'email';
  matchType: 'phone' | 'department' | 'archived' | undefined;
  patientEmail?: string;
  onViewPatientProfile: () => Promise<void>;
};

export const PatientAddedModal = ({
  testID,
  patientId,
  patientName,
  inviteBy,
  matchType,
  patientEmail,
  onViewPatientProfile,
}: PatientAddedModalProps) => {
  const { closeModal } = useModal();

  const onClose = () => {
    closeModal('PatientAdded');
  };

  const handleViewPatientProfile = () => {
    void onViewPatientProfile();
  };

  const handlePrintQR = () => {
    console.warn('TODO: print QR code for patient profile', { patientId });
  };

  const onOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  return (
    <Dialog open onOpenChange={onOpenChange} modal>
      <Dialog.Portal testID={buildTestId({ testID, suffix: 'portal' })}>
        <ModalOverlay testID={buildTestId({ testID, suffix: 'overlay' })} />

        <ModalContent
          testID={testID}
          key="content"
          paddingBottom="$12"
          maxWidth={800}
          alignItems="center"
        >
          <IconValidationValid size="L" color="$support.positive.default" />
          <Text
            type="$h3.strong"
            testID={buildTestId({
              testID,
              suffix: 'title',
            })}
          >
            Patient {matchType === 'archived' ? 'Restored' : 'Added'}
          </Text>

          <Text type="$bodySmall.default" color="$text.60" paddingTop="$3">
            {matchType === 'archived'
              ? `${patientName}’s profile has been restored. They will be notified of the change.`
              : `A new patient profile for ${patientName} has been created.`}
          </Text>

          {inviteBy === 'none' ? null : (
            <Text
              type="$bodySmall.default"
              color="$text.60"
              testID={buildTestId({
                testID,
                suffix: ['invite', 'message'],
              })}
            >
              An invitation has been sent to {patientEmail}.
            </Text>
          )}

          <Text type="$bodySmall.default" color="$text.60" paddingTop="$4">
            The patient can also scan the QR code below to download the app.
          </Text>

          <Image
            source={{
              width: 156,
              height: 156,
              uri: QrWikipedia,
            }}
            width={156}
            height={156}
            marginTop="$4"
            testID={buildTestId({ testID, suffix: ['qr', 'code'] })}
          />

          <Text
            type="$bodySmall.default"
            color="$text.60"
            marginTop={-8}
            testID={buildTestId({
              testID,
              suffix: 'url',
            })}
          >
            [www.url...]
          </Text>
          <Stack gap="$2" marginTop="$6">
            <Button
              variant="secondary"
              label="View Patient Profile"
              onPress={handleViewPatientProfile}
              // @ts-expect-error Parallax doesn't type this prop
              minWidth={256}
              testID={buildTestId({
                testID,
                suffix: ['button', 'patient', 'profile'],
              })}
            />
            <Button
              variant="secondary"
              label="Print"
              iconStart={<IconPrint />}
              onPress={handlePrintQR}
              // @ts-expect-error Parallax doesn't type this prop
              minWidth={256}
              testID={buildTestId({
                testID,
                suffix: ['button', 'print', 'patient', 'profile'],
              })}
            />
            <Button
              variant="primary"
              label="Done"
              onPress={onClose}
              // @ts-expect-error Parallax doesn't type this prop
              minWidth={256}
              testID={buildTestId({
                testID,
                suffix: ['button', 'done'],
              })}
            />
          </Stack>
        </ModalContent>
      </Dialog.Portal>
    </Dialog>
  );
};
